/**
 * Types shared between the API and the web app.
 *
 * The canonical model will live here so both sides reference one definition
 * rather than drifting copies. Empty for now by design — see
 * docs/designs/02-source-data-architecture.md; the shape depends on what the
 * Jobber query list and the ComputerEase ODBC schema actually yield.
 */
// ------------------------------------------------------------------ roles --
/**
 * The roles a signed-in person can hold (D-029, D-109). Additive: a person holds
 * a set, not a rank. They live in Supabase `app_metadata.roles`, which only the
 * admin API can write, and arrive in the access token — so the API reads them
 * from the verified claims and never from the database on the request path.
 *
 *   admin   Settings: accounts and roles, Jobber, the calendar, the handoff
 *           list, imports
 *   staff   everyone in the office — the whole app but Settings. Gates nothing
 *           by itself; every sign-in holds it from its first code (the API
 *           grants it), so an account is never shown with no roles
 *
 * Same values and order as `Roles.All` in the API.
 */
export const ROLES = ['admin', 'staff'];
/** One line per role, in the office's words — for tooltips and the admin page. */
export const ROLE_DESCRIPTIONS = {
    admin: 'Sees Settings: accounts, Jobber, the calendar, handoff, imports.',
    staff: 'Everyone in the office: the whole app except Settings. Every sign-in holds it.',
};
export function isRole(value) {
    return typeof value === 'string' && ROLES.includes(value);
}
/**
 * Reads the role set out of an `app_metadata` object, tolerating anything that
 * is not the shape we write: missing, not an array, or holding strings this
 * build does not know. Unknown values are dropped rather than failing the
 * request, so a role added in a newer deploy does not break an older one.
 * Order is normalised to ROLES order and duplicates removed, so two equal sets
 * compare equal.
 */
export function rolesFrom(appMetadata) {
    if (typeof appMetadata !== 'object' || appMetadata === null)
        return [];
    const raw = appMetadata.roles;
    if (!Array.isArray(raw))
        return [];
    const present = new Set(raw.filter(isRole));
    return ROLES.filter((r) => present.has(r));
}
// ------------------------------------------------------- jobber connection --
/**
 * Two Jobber apps, two connections (D-051): `primary` carries the webhooks,
 * the polls and the schedule; `backfill` is the historical pull, on its own
 * app so its query-cost budget at Jobber is separate. Same account, same
 * callback URL, different client id and secret, different token pair.
 */
export const JOBBER_CONNECTION_PURPOSES = ['primary', 'backfill'];
// --------------------------------------------------------------- schedule --
/**
 * What the schedule is made of (D-038): Visits and Tasks. Jobber also has
 * Events (this account has none) and Assessments (the request inbox — out of
 * scope); neither is pulled or shown.
 */
export const SCHEDULED_ITEM_KINDS = ['visit', 'task'];
/** A person on the schedule — a Jobber team member, projected (D-034). */
/**
 * A person's group: the band their row sits under on the schedule (design 18, D-090); app-owned, one
 * per person, set on Settings → Accounts. `acct_mgr` marks the account managers (D-261).
 */
export const CALENDAR_GROUPS = ['tech', 'shop', 'office', 'acct_mgr'];
export const CALENDAR_GROUP_LABELS = { tech: 'Tech', shop: 'Shop', office: 'Office', acct_mgr: 'AcctMgr' };
/** Every topic the Developer Center offers (read off it 2026-09-24; matches the schema's WebHookTopicEnum). */
export const JOBBER_WEBHOOK_TOPICS = [
    'APP_CONNECT', 'APP_DISCONNECT',
    'CLIENT_CREATE', 'CLIENT_DESTROY', 'CLIENT_UPDATE',
    'EXPENSE_CREATE', 'EXPENSE_DESTROY', 'EXPENSE_UPDATE',
    'INVOICE_CREATE', 'INVOICE_DESTROY', 'INVOICE_UPDATE',
    'JOB_CLOSED', 'JOB_CREATE', 'JOB_DESTROY', 'JOB_UPDATE',
    'MARKETING_ITEM_UPDATE', 'ON_MY_WAY_TRACKING_LINK_REQUEST',
    'PAYMENT_CREATE', 'PAYMENT_DESTROY', 'PAYMENT_UPDATE',
    'PAYOUT_CREATE', 'PAYOUT_DESTROY', 'PAYOUT_UPDATE',
    'PRODUCT_OR_SERVICE_CREATE', 'PRODUCT_OR_SERVICE_DESTROY', 'PRODUCT_OR_SERVICE_UPDATE',
    'PROPERTY_CREATE', 'PROPERTY_DESTROY', 'PROPERTY_UPDATE',
    'QUOTE_APPROVED', 'QUOTE_CREATE', 'QUOTE_DESTROY', 'QUOTE_SENT', 'QUOTE_UPDATE',
    'REQUEST_CREATE', 'REQUEST_DESTROY', 'REQUEST_UPDATE',
    'TIMESHEET_CREATE', 'TIMESHEET_DESTROY', 'TIMESHEET_UPDATE',
    'USER_CREATE', 'USER_UPDATE',
    'VISIT_COMPLETE', 'VISIT_CREATE', 'VISIT_DESTROY', 'VISIT_UPDATE',
];
// ------------------------------------------------------------ to-do lists --
// To-dos replace checklists (design 39, D-228): the default to-dos per kind in
// Settings → To-dos, and a job's lists — its own and one per change order — or
// a quote's one list: the to-dos (`Todo`, the meetings section) whose subject is
// the thing, made from the defaults the first time the list is read. Mirrors
// Core/Contracts/TodoContracts.cs. No money here.
/** The three kinds of default list: a job's, a change order's, a quote's. */
export const TODO_DEFAULT_KINDS = ['job', 'change_order', 'quote'];
/** Whose list a default lands on, resolved when the to-dos are made: the job's project manager (D-199; a change order's is its job's), Jobber's Account Manager field, or the Jobber salesperson — each through the Accounts link; a role with nobody behind it falls down that chain, then to the person opening the list (D-229). */
export const TODO_ASSIGN_TO = ['project_manager', 'account_manager', 'salesperson'];
// --------------------------------------------------------- purchase orders --
// Purchase orders (design 19, slice 2, D-104; lines, receipts by quantity and
// a PO over several jobs since design 26, slice 1), on the Purchase orders
// page. Mirrors apps/api-dotnet/src/UnitedOS.Core/Contracts/PurchaseOrderContracts.cs
// field for field. A PO is a header and one or more lines — a job's Jobber
// line item, a description, a quantity, a unit and a unit price each — and
// may carry lines for several jobs; the PO number is the first line's job
// number and that job's sequence (7739-1, 7739-2), assigned by the app and
// never typed or reused. A receipt is a record with a quantity per line; a
// line's received figure is their sum. Two statuses, derived from the
// figures, never stored as words: Ordered → Partial → Received from the
// receipts; Unpaid → Paid from the office's mark on the PO (D-169: the date,
// the check or ACH number — nothing posted anywhere); Cancelled overrides
// both. Quantities and money are two-place decimal text in the response's one
// currency; dates are ISO date text (2026-10-14); who did what is a name
// through the sign-in's Jobber link, or null. A PO's amount is the sum of its
// lines — a commitment the office typed, never a ledger figure.
/** The three pills: not received whole, not marked paid, everything (cancelled POs count only in All). */
export const PURCHASE_ORDER_VIEWS = ['open', 'unpaid', 'all'];
/**
 * A line's category (D-241, narrowed by D-246): what the line orders. Every line is goods or services,
 * received by quantity; what a vendor adds beyond them is a charge (`PURCHASE_ORDER_CHARGE_KINDS`).
 */
export const PURCHASE_ORDER_LINE_CATEGORIES = ['material', 'labor', 'equipment', 'other'];
/**
 * What a vendor adds to a PO beyond the goods (D-246): a charge's kind. The PO's own — no job, no line
 * item, no quantity, nothing to receive. A discount is the one kind below zero, and never above it.
 */
export const PURCHASE_ORDER_CHARGE_KINDS = ['freight', 'tax', 'tariff', 'surcharge', 'fee', 'discount', 'other'];
/**
 * A PO's kind (design 47, D-300): `invoice_later`, the PO the vendor invoices — every PO before design 47 — or
 * `paid_at_purchase`, a cost already paid (a card, cash, an online order), in the same table with the same numbering.
 * Chosen at creation and never edited. The API's `PurchaseOrderKinds`.
 */
export const PURCHASE_ORDER_KINDS = ['invoice_later', 'paid_at_purchase'];
/** The list's status tabs. Each groups Jobber job statuses; `all` is every one. */
export const JOB_STATUS_TABS = [
    'all',
    'active',
    'requires_invoicing',
    'late',
    'unscheduled',
    'on_hold',
    'archived',
];
/**
 * The job page's tabs, by key. The web arranges them in groups and the URL carries both
 * (`/jobs/7739/accounting/labor`, D-076; `apps/web/src/lib/jobTabs.ts`); the API serves a tab by
 * key. `visits` was `schedule` until 2026-10-01. `change-orders` (2026-10-05) has no endpoint yet: the
 * tab is a placeholder grid until change orders are designed. `billing` (2026-10-08) is Project
 * Management's Details tab — the job's billing data, read through `/api/jobs/{number}/billing`.
 * `general` (design 46) is General as one tab: the cards, the Fields card (`/api/jobs/{number}/fields`)
 * and the notes (`/api/jobs/{number}/communications`) — its Notes and Details sub tabs before it.
 */
export const JOB_TABS = [
    'general',
    'line-items',
    'documents',
    'billing',
    'to-dos',
    'schedule-of-values',
    'purchase-orders',
    'plan',
    'change-orders',
    'labor',
    'financials',
    'visits',
    'delivery',
];
// ---------------------------------------------------------------- quotes --
// The quotes list and the quote page (design 27, D-060). Mirrors
// apps/api-dotnet/src/UnitedOS.Core/Contracts/QuoteContracts.cs field for field. A quote is
// Jobber's own kind — its own number (text in Jobber; digits on every quote the customer has),
// six statuses, line items, custom fields and notes — never a job with a new type. The tabs it
// shares with a job (notes, Jobber's files, the custom fields) reuse the job's shapes.
/** The list's status tabs: Jobber's QuoteStatusTypeEnum exactly (introspected 2026-10-10), plus `all`. */
export const QUOTE_STATUS_TABS = ['all', 'draft', 'awaiting_response', 'changes_requested', 'approved', 'converted', 'archived'];
/** The quote page's tabs, by key; the web groups them (`apps/web/src/lib/quoteTabs.ts`). `general` is General as one tab (design 46: the cards, the Fields card from `GET /api/quotes/{number}/fields`, the notes), `worksheet` the app-owned worksheet (`GET /api/quotes/{number}/worksheet`, D-144), `to-dos` the quote's to-dos; the lines and the files are `quote-tab`'s. */
export const QUOTE_TABS = ['general', 'to-dos', 'line-items', 'worksheet', 'documents'];
/** The kinds whose to-dos are a list of their own (design 39; a PO's since design 46, a PO draft's since design 50) — `TodoSubjectRules.ListKinds` in the API. */
export const TODO_LIST_KINDS = ['job', 'change_order', 'quote', 'purchase_order', 'purchase_order_draft'];
// ---------------------------------------------------------- live meetings --
// Meetings, slice 2 (design 19): running a meeting. Mirrors Core/Contracts/LiveMeetingContracts.cs.
// The server keeps the clock; the screen ticks on from `clock.serverNow` and re-reads when the
// occurrence's `revision` moves.
/** `triage` is Keep Kill Combine, `issues` IDS — two steps since MeetingsTasks §14. */
export const AGENDA_SECTIONS = ['segue', 'scorecard', 'priorities', 'headlines', 'todos', 'triage', 'issues', 'conclude'];
// -------------------------------------------------------------- GC billing --
// GC billing, slice 1 (design 20, D-113): Billing setup per job and the GC
// billing list that replaces the office's GC Jobs reconciliation sheet.
// Mirrors apps/api-dotnet/src/UnitedOS.Core/Contracts/GcBillingContracts.cs
// field for field. Jobber's figures are Jobber's and say so (the money band's
// rule); the computed ones name their rule in `rounding`. Money is two-place
// decimal text in the response's one currency; dates ISO date text. Nothing
// here is a ledger figure.
/** The five pills: everything; due within the window; needing attention; fully billed; closed. */
export const JOB_BILLING_VIEWS = ['all', 'due', 'attention', 'billed', 'closed'];
/** The Billing page's kind tabs: every kind, GC, PM. */
export const JOB_BILLING_KIND_TABS = ['all', 'gc', 'pm'];
//# sourceMappingURL=index.js.map